import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { HttpClient } from '@angular/common/http';
import { mergeMap } from 'rxjs';
import { AuthService } from './auth.service';
import { DesktopPaymentResponse } from './desktop-payments.service';
import { ApiPaymentResponse } from './api-payments.service';
import { DesPaymentResponse } from './des-credit-purchases.service';

type AuthorizeNetPaymentRequest = {
  opaque_data: {
    datDescriptor: string;
    dataValue: string;
  };
  invoice_id: string;
};

type AuthorizeNetApiPaymentRequest = {
  opaque_data: {
    datDescriptor: string;
    dataValue: string;
  };
  api_invoice_id: string;
};

type SavedMethodPaymentRequest = {
  invoice_id: string;
  saved_payment_method_id: string;
};

type SavedMethodApiPaymentRequest = {
  api_invoice_id: string;
  saved_payment_method_id: string;
};

type AuthorizeNetDesPaymentRequest = {
  opaque_data: {
    datDescriptor: string;
    dataValue: string;
  };
  des_credit_purchase_id: string;
};

type SavedMethodDesPaymentRequest = {
  des_credit_purchase_id: string;
  saved_payment_method_id: string;
};

export type AuthorizeNetSavedPaymentMethod = {
  id: string;
  tenant_id: string;
  payment_type: 'card' | 'bank';
  last_4_digits: string;
  card_expiration?: string;
  card_type?: string;
  bank_account_type?: string;
  name_on_account?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  is_default: boolean;
  is_api_default: boolean;
  created_at: string;
  updated_at: string;
};

type CreateSavedPaymentMethodRequest = {
  opaque_data: {
    datDescriptor: string;
    dataValue: string;
  };
  bill_to: {
    firstName?: string;
    lastName?: string;
    company?: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  tenant_id: string;
  is_default: boolean;
};

export type GetSavedPaymentMethodsResponse = {
  payment_profiles: AuthorizeNetSavedPaymentMethod[];
};

type CreateSavedPaymentMethodResponse = {
  payment_profile: AuthorizeNetSavedPaymentMethod;
};

type SetDefaultPaymentMethodResponse = {
  message: string;
};

export type PaymentStatus =
  | 'created'
  | 'processing'
  | 'failed'
  | 'declined'
  | 'held'
  | 'completed'
  | 'cancelled'
  | 'refunding'
  | 'refunded';

@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  private processPaymentUrl =
    environment.serverUrl + '/payments/process-authorizenet-payment';
  private processApiPaymentUrl =
    environment.serverUrl + '/payments/process-api-authorizenet-payment';
  private processPaymentWithSavedMethodUrl =
    environment.serverUrl + '/payments/process-payment-with-saved-method';
  private processApiPaymentWithSavedMethodUrl =
    environment.serverUrl + '/payments/process-api-payment-with-saved-method';
  private processDesPaymentUrl =
    environment.serverUrl + '/payments/process-des-authorizenet-payment';
  private processDesPaymentWithSavedMethodUrl =
    environment.serverUrl + '/payments/process-des-payment-with-saved-method';
  private savedPaymentMethodsForTenantUrl =
    environment.serverUrl + '/payments/saved-payment-methods-for-tenant';
  private createSavedPaymentMethodUrl =
    environment.serverUrl + '/payments/save-payment-method';
  private setDefaultPaymentMethodUrl =
    environment.serverUrl + '/payments/set-default-payment-method';
  private setApiDefaultPaymentMethodUrl =
    environment.serverUrl + '/payments/set-api-default-payment-method';
  private deletePaymentMethodUrl =
    environment.serverUrl + '/payments/delete-saved-payment-method';

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private http: HttpClient
  ) {}

  processAuthorizeNetPayment(req: AuthorizeNetPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopPaymentResponse>(
          this.processPaymentUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  processApiAuthorizeNetPayment(req: AuthorizeNetApiPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiPaymentResponse>(
          this.processApiPaymentUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  processPaymentWithSavedMethod(req: SavedMethodPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopPaymentResponse>(
          this.processPaymentWithSavedMethodUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  processApiPaymentWithSavedMethod(req: SavedMethodApiPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiPaymentResponse>(
          this.processApiPaymentWithSavedMethodUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  processDesAuthorizeNetPayment(req: AuthorizeNetDesPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesPaymentResponse>(
          this.processDesPaymentUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  processDesPaymentWithSavedMethod(req: SavedMethodDesPaymentRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesPaymentResponse>(
          this.processDesPaymentWithSavedMethodUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  getCurrentUserSavedPaymentMethods() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const tenantId = this.authService.currentUser?.tenant_id;
        if (!tenantId) {
          throw new Error('No tenant ID found');
        }
        return this.http.get<GetSavedPaymentMethodsResponse>(
          `${this.savedPaymentMethodsForTenantUrl}/${tenantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  getSavedPaymentMethods(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<GetSavedPaymentMethodsResponse>(
          `${this.savedPaymentMethodsForTenantUrl}/${tenantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  createNewPaymentMethod(req: CreateSavedPaymentMethodRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<CreateSavedPaymentMethodResponse>(
          this.createSavedPaymentMethodUrl,
          req,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  setDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<SetDefaultPaymentMethodResponse>(
          `${this.setDefaultPaymentMethodUrl}/${paymentMethod.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  setApiDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<SetDefaultPaymentMethodResponse>(
          `${this.setApiDefaultPaymentMethodUrl}/${paymentMethod.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  deleteSavedPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.delete(
          `${this.deletePaymentMethodUrl}/${paymentMethod.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  getBankAccountTypeLabel(bankAccountType: string) {
    switch (bankAccountType) {
      case 'checking':
        return 'Checking';
      case 'savings':
        return 'Savings';
      case 'businessChecking':
        return 'Business Checking';
      default:
        return 'Bank';
    }
  }
}
